import type { ViewFieldsFragment } from '@salescore/client-api'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewQueryList,
} from '@salescore/core'
import { Number, pipe } from 'effect'
import { type ReactElement, type RefObject, useEffect, useMemo } from 'react'

import { buildHeaderHeight, type RSheetColumn, type RSheetRecordNode } from '../../../domain/column'
import { ROW_INDEX_COLUMN_WIDTH } from '../../../hooks/useSheetSystemColumns'
import type { RSheetContext } from '../../../recoil/context'
import { type CursorPosition, useCalculatePosition, useCursorModel, useCursorSelector } from '../../../recoil/cursor'
import { useCalculatedRowHeight } from '../../../recoil/navigation/hooks'
import { useRecordsImpl } from '../../../recoil/records'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import { getRecordNodeWithInnerRowIndex } from '../../util/innerRowRelated'
import { ContextMenu } from './ContextMenu'
import { CopyingCursorBox } from './CopyingCursorBox'
import { ExpandCursorBox } from './ExpandCursorBox'
import { InputCursorBox } from './InputCursorBox'
import { MainCursorBox } from './MainCursorBox'

/**
 * シートでセルをクリックしたときに表示される青い枠 (= カーソル) を、`MainCursorBox` /
 * `ExpandCursorBox` / `CopyingCursorBox` / `InputCursorBox` / `ContextMenu` の組み合わせで
 * 描画するコンポーネント。
 *
 * カーソルは各セル側で `border` を引くのではなく、別レイヤーに `position: absolute` で
 * 浮かせて実装しています。理由:
 *
 * - セル側で実装すると z-index に影響され、最前面にならないケースがある
 * - セル側で `border` を引くと `width` が変わって横幅調整が必要になる
 * - カーソル実装が混じるとセル側のロジックが複雑になる
 * - カーソルのアニメーションを実装したかった
 *
 * セルが入力モードのときに表示される `InputCursorBox` を独立させているのも同じ理由で、
 * セル側のボディの zIndex に縛られず常に最前面に出すためです。
 */
export const RSheetsCursorBox = ({
  asFixed,
  rootReference,
  context,
  columns,
  compiledConfig,
  view,
  setDragging,
  ability,
  query,
  visibleRowNumber,
  setHasChangeToViewSchema,
}: {
  asFixed: boolean
  rootReference: RefObject<HTMLDivElement>
  context: RSheetContext
  columns: Array<RSheetColumn<RSheetRecordNode>>
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  setDragging: () => void
  ability: ViewAbility & SalescoreObjectAbility
  query: ViewQueryList | undefined
  visibleRowNumber: number | undefined
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
  // eslint-disable-next-line complexity
}): ReactElement => {
  const { headerHeight } = useMemo(() => buildHeaderHeight(columns, context), [columns, context])
  // シートの場合のみ、右端が揃ってしまうとテキスト編集する際に右に伸びる編集領域が見えず使い辛くなるためオフセットを用意
  const cursor = useCursorModel(columns)
  const { copiedArea, expandArea, position, fixedLeftColumnIndex, fixedLeftColumnWidth } = useCursorSelector(
    context.fixedLeftColumnIndex,
    columns,
    compiledConfig,
    visibleRowNumber,
  )

  const rows = useRecordsImpl()
  const calculatePosition = useCalculatePosition(columns, compiledConfig, visibleRowNumber)
  const calculatedRowHeight = useCalculatedRowHeight(compiledConfig)

  const movedCursorPosition = useMemo(() => {
    if (cursor === undefined) return
    const rowId = rows[cursor.main.rowIndex]?.id
    const column = columns[cursor.main.columnIndex]
    if (rowId === undefined || column === undefined) return
    return calculatePosition(cursor.expand ?? cursor.main)
  }, [cursor, rows, columns, calculatePosition])

  const { x, y, width, height } = movedCursorPosition ?? {}

  useEffect(() => {
    if (x === undefined || y === undefined || width === undefined || height === undefined) return
    if (rootReference.current === null || fixedLeftColumnWidth === undefined) return
    handleScroll({
      x,
      y,
      width,
      height,
      fixedLeftColumnWidth,
      rowHeight: calculatedRowHeight,
      rootDivElement: rootReference.current,
      headerHeight,
    })
  }, [x, y, width, height, fixedLeftColumnWidth, headerHeight, rootReference, calculatedRowHeight])

  if (cursor === undefined) {
    return <></>
  }

  const column = columns[cursor.main.columnIndex]
  const row = rows[cursor.main.rowIndex]
  if (column === undefined || row === undefined) {
    return <></>
  }

  const isFixedColumnCursor = cursor.main.columnIndex < (fixedLeftColumnIndex ?? 0)

  // 固定カラム上には固定カラムのカーソルのみ、固定カラム外では固定カラム外のカーソルのみを表示したい（表示されるカーソルは常に1つ）
  // 例外的に、ExpandCursorBoxは固定カラム内外で分割して表示する
  if (asFixed !== isFixedColumnCursor) {
    return (
      <div id="cursor-box-1">
        {expandArea !== undefined && (
          <ExpandCursorBox asFixed={asFixed} expandArea={expandArea} headerHeight={headerHeight} />
        )}
        {copiedArea !== undefined && cursor.copying?.visible !== false && (
          <CopyingCursorBox asFixed={asFixed} copiedArea={copiedArea} headerHeight={headerHeight} />
        )}
      </div>
    )
  }

  if (cursor.editing?.isEditing === true) {
    const recordNodeWithInnerRowIndex = getRecordNodeWithInnerRowIndex(row, column.node, cursor.main.innerRowIndex)

    return (
      <div id="cursor-box-2">
        {position !== undefined && recordNodeWithInnerRowIndex !== undefined && (
          <InputCursorBox
            rootReference={rootReference}
            cursor={cursor}
            column={column}
            row={row}
            position={position}
            recordNodeWithInnerRowIndex={recordNodeWithInnerRowIndex}
            headerHeight={headerHeight}
            fixedLeftColumnIndex={context.fixedLeftColumnIndex}
            columns={columns}
            compiledConfig={compiledConfig}
            ability={ability}
            query={query}
            visibleRowNumber={visibleRowNumber}
          />
        )}
      </div>
    )
  }

  return (
    <div id="cursor-box-3">
      {expandArea !== undefined && (
        <ExpandCursorBox asFixed={asFixed} expandArea={expandArea} headerHeight={headerHeight} />
      )}
      {copiedArea !== undefined && cursor.copying?.visible !== false && (
        <CopyingCursorBox asFixed={asFixed} copiedArea={copiedArea} headerHeight={headerHeight} />
      )}
      {position !== undefined && expandArea === undefined && fixedLeftColumnIndex !== undefined && (
        <MainCursorBox
          cursor={cursor}
          position={position}
          headerHeight={headerHeight}
          fixedLeftColumnIndex={fixedLeftColumnIndex}
          context={context}
          column={column}
          columns={columns}
          compiledConfig={compiledConfig}
          setDragging={setDragging}
          ability={ability}
          query={query}
          visibleRowNumber={visibleRowNumber}
        />
      )}
      <ContextMenu
        rootReference={rootReference}
        context={context}
        columns={columns}
        compiledConfig={compiledConfig}
        view={view}
        ability={ability}
        query={query}
        visibleRowNumber={visibleRowNumber}
        setHasChangeToViewSchema={setHasChangeToViewSchema}
      />
    </div>
  )
}

export const handleScroll = ({
  x: cursorLeftX,
  y: cursorTopY,
  width: cursorWidth,
  height: cursorHeight,
  fixedLeftColumnWidth,
  rowHeight,
  rootDivElement,
  headerHeight,
}: CursorPosition & {
  fixedLeftColumnWidth: number
  rowHeight: number
  headerHeight: number
  rootDivElement: HTMLDivElement
}) => {
  // not-fixed内でのカーソル位置
  // rootDivElementはfixed, not-fixedを含むテーブル全体の要素なのでfixed分引いておく
  const cursor = {
    topLeft: { x: cursorLeftX - fixedLeftColumnWidth, y: cursorTopY },
    bottomRight: { x: cursorLeftX - fixedLeftColumnWidth + cursorWidth, y: cursorTopY + cursorHeight },
  }

  // ヘッダー, フッターを含めないよう引いておく
  const viewportHeight = rootDivElement.clientHeight - headerHeight - rowHeight
  const viewportWidth = rootDivElement.clientWidth - fixedLeftColumnWidth
  const areaLeftX = rootDivElement.scrollLeft
  const areaTopY = rootDivElement.scrollTop

  const viewport = {
    topLeft: { x: areaLeftX, y: areaTopY },
    bottomRight: { x: areaLeftX + viewportWidth, y: areaTopY + viewportHeight },
  }

  if (
    viewport.topLeft.y <= cursor.topLeft.y &&
    cursor.bottomRight.y <= viewport.bottomRight.y &&
    viewport.topLeft.x <= cursor.topLeft.x &&
    cursor.bottomRight.x <= viewport.bottomRight.x
  ) {
    // カーソルが表示領域内にある場合は自動スクロール不要
    return
  }

  const top = pipe(viewport.topLeft.y, Number.max(cursor.bottomRight.y - viewportHeight), Number.min(cursor.topLeft.y))
  if (cursorLeftX < fixedLeftColumnWidth) {
    // 固定列内では左右スクロールしない
    rootDivElement.scrollTo({ top, behavior: undefined })
    return
  }

  const normalLeft = pipe(
    viewport.topLeft.x,
    Number.max(cursor.bottomRight.x - viewportWidth),
    Number.min(cursor.topLeft.x),
  )
  // checkboxまでを表示するように自動スクロールする場合はrowIndexも表示するところまで自動スクロールする
  // （rowIndexまで選択可とする構想も出ているため暫定対応としている）
  const left = normalLeft !== viewport.topLeft.x && normalLeft === ROW_INDEX_COLUMN_WIDTH ? 0 : normalLeft
  rootDivElement.scrollTo({ top, left, behavior: undefined })
}
